import { routes } from '../site';

/**
 * Entegrasyonlar sayfası (/entegrasyonlar). Metinler "ZHesap Site
 * Sayfaları - Metinler.docx", marka listeleri Zhesa Dark Landing
 * tasarımından.
 */
export interface IntegrationBrand {
  name: string;
  /** public/ altındaki logo dosyası; yoksa marka adı yazı olarak çizilir. */
  logo?: string;
}

/**
 * Marka listesi CMS'ten gelir (admin → Entegrasyonlar; `useIntegrations()`); buradaki liste
 * CMS'e ulaşılamadığında kullanılan yedektir. Kategorinin rengi ve ikonu ise tasarımın parçası,
 * CMS'teki kategori anahtarı (`id`) ile eşlenir.
 */
export interface IntegrationCategory {
  id: string;
  title: string;
  desc: string;
  color: string;
  rgb: string;
  icon: string;
  brands: IntegrationBrand[];
}

const b = (name: string, logo?: string): IntegrationBrand => ({ name, logo });

export const entegrasyonlarPage = {
  href: routes.entegrasyonlar,
  hero: {
    crumb: 'Entegrasyonlar',
    title: 'Veriyi sen taşıma.',
    titleAccent: 'ZHesap toplasın.',
    lead: 'ERP’nden ürün maliyetini, pazaryerlerinden satış ve kesintileri, e-ticaret altyapından siparişleri, kargo firmalarından gönderi maliyetlerini ZHesap tek yerde bir araya getirir.',
    primary: { label: '7 Gün Ücretsiz Dene', href: '/#erken-erisim' },
    // Aşağıdakiler "ZHesap Yeni Menü.xlsx" Entegrasyonlar sekmesindeki düzenden.
    secondary: { label: 'Entegrasyonları Keşfet', href: '#sistemler' },
    features: [
      { icon: 'database', title: 'Otomatik veri akışı', desc: 'Manuel işlem yok' },
      { icon: 'clock', title: 'Güncel ve doğru veri', desc: 'Her zaman senkron' },
      { icon: 'shieldCheck', title: 'Tüm operasyon tek ekranda', desc: 'Kârlılık, stok, kampanya, mutabakat' },
    ] as const,
    visualLabel:
      'Pazaryeri, e-ticaret altyapısı, ERP ve kargo verileri ZHesap’ta birleşir; kârlılık, stok, finans, kampanya ve rakip analizine dönüşür',
    /** Akışın sağ ucundaki ürün modülleri. */
    outputs: ['Kârlılık Analizi', 'Stok Zekâsı', 'Finans & Mutabakat', 'Kampanya Kârlılığı', 'Rakip & Sektör Analizi'],
  },
  /**
   * Hero görseli ve "Doğru veri" satırındaki dört veri kaynağı. `examples`
   * hero kartındaki marka örnekleri, `outcome` kaynağın ZHesap'ta neye
   * dönüştüğü (Excel düzeninden).
   */
  sources: [
    { title: 'Pazaryeri', desc: 'Sipariş, komisyon, kesinti, kampanya', examples: 'Trendyol, Hepsiburada, Amazon, n11…', outcome: 'Kârlılık & hakediş kontrolü', color: '#3b82f6', rgb: '59,130,246' },
    { title: 'E-ticaret Altyapısı', desc: 'Sipariş, ürün, müşteri verileri', examples: 'Ticimax, ikas, T-Soft, Ideasoft…', outcome: 'Kanal bazlı performans', color: '#45c0ee', rgb: '69,192,238' },
    { title: 'ERP', desc: 'Ürün maliyeti, stok, ürün bilgileri', examples: 'Nebim, Logo, Mikro, Netsis…', outcome: 'Gerçek ürün kârlılığı', color: '#a78bfa', rgb: '139,92,246' },
    { title: 'Kargo', desc: 'Gönderi maliyeti, desi, teslimat', examples: 'Yurtiçi, Aras, Sürat, UPS, DHL…', outcome: 'Kargo mutabakatı', color: '#fbbf24', rgb: '251,191,36' },
  ],
  /** "Doğru veri" kartlarının sırası metin dosyasındaki gibi: ERP, Pazaryeri, Kargo, E-ticaret. */
  dataOrder: [2, 0, 3, 1],
  systems: {
    title: 'Kullandığın sistemlerle çalışır.',
    desc: 'E-ticaret ekosistemindeki büyük platformlarla entegrasyonlarımız hazır.',
    /** Kart kapalıyken görünen marka sayısı. */
    visible: 6,
    more: (hidden: number) => `+ daha fazlası (${hidden})`,
    less: 'Daha az',
    all: 'Tümü',
    searchLabel: 'Entegrasyon ara',
    searchPlaceholder: 'Entegrasyon ara (ör. Trendyol, Nebim)',
    tabsLabel: 'Kategori',
    empty: (q: string) => `“${q}” için bir entegrasyon bulamadık.`,
    emptyCta: 'Entegrasyon Talep Et',
    categories: [
      {
        id: 'pazaryeri',
        title: 'Pazaryerleri',
        desc: 'Online pazaryeri satış verileri, komisyon ve kesintiler.',
        color: '#60a5fa',
        rgb: '59,130,246',
        icon: 'M3 4h2l2.4 11h10.2L20 8H6.2M9 20h.01M17 20h.01',
        brands: [
          b('Trendyol', 'assets/logos/trendyol.svg'),
          b('Hepsiburada', 'assets/logos/hepsiburada.svg'),
          b('Amazon'),
          b('n11'),
          b('Çiçeksepeti'),
          b('Beymen'),
          b('Boyner'),
          b('Idefix'),
          b('Pazarama'),
          b('Modanisa'),
          b('ePttAVM'),
          b('LCWaikiki'),
          b('Teknosa'),
          b('Pasaj'),
          b('Temu Türkiye'),
        ],
      },
      {
        id: 'eticaret',
        title: 'E-ticaret Altyapıları',
        desc: 'Mağaza siparişleri, ürün bilgileri ve müşteri verileri.',
        color: '#45c0ee',
        rgb: '69,192,238',
        icon: 'M3 5h18v12H3zM8 21h8M12 17v4',
        brands: [
          b('Ticimax', 'assets/logos/ticimax.svg'),
          b('İkas', 'assets/logos/ikas.svg'),
          b('T-Soft', 'assets/logos/tsoft.svg'),
          b('Ideasoft'),
          b('Shopify', 'assets/logos/shopify.svg'),
          b('WooCommerce'),
          b('Livasoft'),
        ],
      },
      {
        id: 'erp',
        title: 'ERP',
        desc: 'Ürün maliyeti, stok, cari ve operasyonel veriler.',
        color: '#a78bfa',
        rgb: '139,92,246',
        icon: 'M4 6c0-1.7 3.6-3 8-3s8 1.3 8 3-3.6 3-8 3-8-1.3-8-3zM4 6v12c0 1.7 3.6 3 8 3s8-1.3 8-3V6M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3',
        brands: [
          b('Nebim', 'assets/logos/nebim.png'),
          b('Logo', 'assets/logos/logo.svg'),
          b('Mikro'),
          b('Uyumsoft'),
          b('Netsis'),
          b('LucaNet'),
          b('Dia'),
          b('Sentos'),
          b('Entegra'),
          b('Paraşüt'),
          b('Odoo'),
          b('Bizim Hesap'),
          b('NetKasam'),
        ],
      },
      {
        id: 'kargo',
        title: 'Kargo',
        desc: 'Gönderi maliyetleri, desi ve teslimat verileri.',
        color: '#fbbf24',
        rgb: '251,191,36',
        icon: 'M2 7h11v9H2zM13 10h4l4 3v3h-8M6 20a2 2 0 100-4 2 2 0 000 4M17 20a2 2 0 100-4 2 2 0 000 4',
        brands: [
          b('DHL ECommerce'),
          b('Sürat Kargo'),
          b('Aras Kargo'),
          b('UPS'),
          b('Yurtiçi Kargo'),
          b('Kolay Gelsin'),
        ],
      },
    ] as IntegrationCategory[],
  },
  data: {
    title: 'Entegrasyon değil.',
    titleAccent: 'Doğru karar için doğru veri.',
    lead: 'Farklı sistemlerdeki verileri bir araya getirir, e-ticaret operasyonunun tamamını anlamana yardımcı olur.',
  },
  excel: {
    title: 'Excel’e maliyet girmek',
    titleAccent: 'zorunda değilsin.',
    desc: 'Ürün maliyeti, satışlar, komisyonlar, kargo ve tüm kesintiler entegrasyonlar sayesinde otomatik olarak ZHesap’a gelir. Sen sadece sonucu görürsün.',
    /** Örnek sipariş: kalemler kaynaklarından sırayla gelir, net kâr hesaplanır. */
    example: {
      label: 'Örnek sipariş · Trendyol',
      inputs: [
        { source: 'Nebim', kind: 'ERP', label: 'Ürün maliyeti', amount: 427.5, color: '#a78bfa' },
        { source: 'Trendyol', kind: 'Pazaryeri', label: 'Satış fiyatı', amount: 1199.9, color: '#3b82f6', revenue: true },
        { source: 'Trendyol', kind: 'Pazaryeri', label: 'Komisyon ve kesintiler', amount: 215.98, color: '#3b82f6' },
        { source: 'Yurtiçi Kargo', kind: 'Kargo', label: 'Kargo maliyeti', amount: 82.4, color: '#fbbf24' },
      ],
      result: { title: 'ZHesap', gross: 'Brüt satış', net: 'Net kâr' },
    },
  },
  cta: {
    title: 'Kullandığın sistem ZHesap’ta var mı?',
    desc: 'Entegrasyonlarımızı incele; aradığını bulamazsan bize ilet, birlikte değerlendirelim.',
    // Ayrı bir entegrasyon talep ucu yok; talep iletişim formundan gelir.
    button: { label: 'Entegrasyon Talep Et', href: routes.iletisim },
    found: (n: number) => `${n} entegrasyon bulundu.`,
    showInList: 'Listede gör',
    notFound: 'Bulamadık; talebini ilet, birlikte değerlendirelim.',
  },
} as const;
